import { closeDialog, debounce, parseHTML, routes } from 'theme';

/**
 * Search suggestions for snippets/search-form.liquid, following the combobox pattern: focus stays in the field, the arrow keys
 * move through the suggestions, Enter opens the highlighted one, and Escape closes the list. Without this script, the field is
 * a plain search form.
 */
if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class extends HTMLElement {
      /** @type {HTMLInputElement|null} */
      #input = null;
      /** @type {HTMLElement|null} */
      #results = null;
      /** @type {AbortController|undefined} */
      #request;
      #cache = new Map();

      connectedCallback() {
        this.#input = this.querySelector('input[type="search"]');
        this.#results = this.querySelector('[data-predictive-results]');
        if (!this.#input || !this.#results) return;
        this.#input.addEventListener('input', this.#onInput);
        this.#input.addEventListener('keydown', this.#onKeydown);
        this.#input.addEventListener('focus', this.#onFocus);
        this.addEventListener('focusout', this.#onFocusOut);
      }

      disconnectedCallback() {
        this.#request?.abort();
      }

      #onInput = debounce(() => this.#search(this.#input.value.trim()), 300);

      #onFocus = () => {
        if (this.#input.value.trim() && this.#results.childElementCount) this.#open();
      };

      #onFocusOut = () => {
        // Waits for focus to land, so moving to a suggestion with the pointer doesn't close the list first.
        setTimeout(() => {
          if (!this.contains(document.activeElement)) this.#close();
        });
      };

      async #search(terms) {
        this.#request?.abort();
        if (!terms) {
          this.#close();
          return;
        }

        const url = new URL(routes.predictiveSearch, window.location.href);
        url.searchParams.set('q', terms);
        url.searchParams.set('resources[type]', 'query,product,collection,page,article');
        url.searchParams.set('resources[limit]', '4');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', 'predictive-search');

        try {
          let html = this.#cache.get(url.href);
          if (html === undefined) {
            this.#request = new AbortController();
            const response = await fetch(url, { signal: this.#request.signal });
            if (!response.ok) throw new Error(String(response.status));
            html = await response.text();
            this.#cache.set(url.href, html);
          }
          this.#render(html);
        } catch (error) {
          if (error.name !== 'AbortError') this.#close();
        }
      }

      #render(html) {
        const source = parseHTML(html).querySelector('.shopify-section');
        if (!source) return;
        // Two search fields can show suggestions on one page, so each list gets its own option IDs.
        for (const option of source.querySelectorAll('[role="option"]')) option.id = `${this.#input.id}-${option.id}`;
        this.#results.replaceChildren(...source.childNodes);
        this.#open();
      }

      #options() {
        return [...this.#results.querySelectorAll('[role="option"]')];
      }

      #highlight(option) {
        for (const other of this.#options()) other.setAttribute('aria-selected', String(other === option));
        if (option) {
          this.#input.setAttribute('aria-activedescendant', option.id);
          option.scrollIntoView({ block: 'nearest' });
        } else {
          this.#input.removeAttribute('aria-activedescendant');
        }
      }

      #onKeydown = (event) => {
        if (this.#results.hidden) {
          // A search field clears itself on Escape before its dialog would close, so the panel closes here instead.
          const dialog = this.closest('dialog');
          if (event.key === 'Escape' && dialog?.open) {
            event.preventDefault();
            closeDialog(dialog);
          }
          return;
        }
        const options = this.#options();
        const current = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          if (!options.length) return;
          const step = event.key === 'ArrowDown' ? 1 : -1;
          this.#highlight(options[(current + step + options.length) % options.length]);
        } else if (event.key === 'Enter' && current >= 0) {
          event.preventDefault();
          options[current].click();
        } else if (event.key === 'Escape') {
          // The first Escape closes the list; the next one can close the search panel.
          event.preventDefault();
          event.stopPropagation();
          this.#close();
        }
      };

      #open() {
        this.#results.hidden = false;
        this.#input.setAttribute('aria-expanded', 'true');
      }

      #close() {
        this.#results.hidden = true;
        this.#input.setAttribute('aria-expanded', 'false');
        this.#highlight(null);
      }
    },
  );
}
